"use client";

import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Chart, ChartData, ChartOptions } from "chart.js/auto";
import merge from "lodash/merge";
import { ComponentProps } from "react";
import { Bar } from "react-chartjs-2";
import { twMerge } from "tailwind-merge";

import { Translation } from "@/data/lang";
import { useLang } from "@/hooks/use-lang";

import { getChipComboChartOptions } from "../helpers/chip-combo-chart-options";

Chart.register();

type ChipComboChartProps = {
  data: ChartData<"bar">;
  options?: ChartOptions<"bar">;
} & ComponentProps<"figure">;

export const ChipComboChart = ({
  data,
  options,
  ...props
}: ChipComboChartProps) => {
  const { t } = useLang();
  const chipBarChartOptions = getChipComboChartOptions({ t });
  return (
    <figure
      {...props}
      className={twMerge(props.className, "flex w-full flex-col items-center")}
    >
      <Bar data={data} options={merge(chipBarChartOptions, options)} />
      <CustomLegend data={data} t={t} />
    </figure>
  );
};

const CustomLegend = ({
  data,
  t,
}: {
  data: ChartData<"bar">;
  t: Translation;
}) => (
  <div className="flex flex-wrap items-center justify-center gap-2">
    {Array.from(data.datasets)
      .sort((a, b) => {
        if (a.label === t.analysis.chip.institutionalInvestors.price) return 1;
        if (b.label === t.analysis.chip.institutionalInvestors.price) return -1;
        return 0;
      })
      .map((dataset, index) => (
        <div
          key={index}
          className="flex items-center gap-1 text-xs font-medium"
        >
          <CheckCircleIcon
            className="h-5 w-5"
            style={{
              color: dataset.borderColor as string,
            }}
          />
          <span>{dataset.label}</span>
        </div>
      ))}
  </div>
);
